import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useAuthenticatedFetch } from './useAuthenticatedFetch';
import { useCurrentUser } from './useApi';
import { useUserEntitlements } from './useUserEntitlements';
import { marketAccessCache } from '../services/market-access-cache';
import { API_URL } from '../utils/config';

interface MarketAccessInfo {
  hasAccess: boolean;
  accessLevel?: 'market' | 'country' | 'continent' | 'market_grouping';
  validUntil?: string;
}

interface MarketAccessState {
  [marketId: number]: MarketAccessInfo;
}

interface UseMarketAccessOptions {
  enabled?: boolean;
  refetchInterval?: number;
  onError?: (error: Error) => void;
}

export function useMarketAccess(
  marketIds: number[],
  options: UseMarketAccessOptions = {}
) {
  const { enabled = true, refetchInterval, onError } = options;
  const [accessState, setAccessState] = useState<MarketAccessState>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const { authenticatedFetch } = useAuthenticatedFetch();
  const { data: user } = useCurrentUser();
  const abortControllerRef = useRef<AbortController | undefined>(undefined);
  const refetchIntervalRef = useRef<number | undefined>(undefined);

  // Memoize market IDs to prevent unnecessary re-renders
  const marketIdsString = marketIds.join(',');
  const memoizedMarketIds = useMemo(
    () => [...marketIds].sort((a, b) => a - b),
    [marketIdsString]
  );

  // Memoize filters object to prevent infinite loop
  const entitlementsFilters = useMemo(() => ({}), []);

  // Fetch user entitlements to check access
  const { data: entitlementsData, loading: entitlementsLoading } = useUserEntitlements({
    filters: entitlementsFilters,
    enabled: enabled && !!user,
  });

  // Compute access state from entitlements data using useMemo to avoid re-computation
  const computedAccessState = useMemo(() => {
    if (!user || !enabled || memoizedMarketIds.length === 0 || !entitlementsData) {
      return {};
    }

    const newState: MarketAccessState = {};

    for (const marketId of memoizedMarketIds) {
      // Check if user has an active entitlement for this market
      const hasEntitlement = entitlementsData?.entitlements?.some((e: any) => {
        // Check for direct market access (check both marketId field and market.id)
        if (e.accessLevel === 'market' && (e.marketId === marketId || e.market?.id === marketId)) {
          return ['active', 'expiring'].includes(e.status);
        }

        // Check for country-level access
        if (e.accessLevel === 'country' && e.countryId) {
          return ['active', 'expiring'].includes(e.status);
        }

        // Check for continent-level access
        if (e.accessLevel === 'continent' && e.continentId) {
          return ['active', 'expiring'].includes(e.status);
        }

        // Check for market grouping access
        if (e.accessLevel === 'market_grouping' && e.marketGroupingId) {
          return ['active', 'expiring'].includes(e.status);
        }

        return false;
      }) || false;

      // Find the entitlement to get access level and valid until
      const entitlement = entitlementsData?.entitlements?.find((e: any) => {
        if (e.accessLevel === 'market' && (e.marketId === marketId || e.market?.id === marketId)) {
          return ['active', 'expiring'].includes(e.status);
        }
        return false;
      });

      const validUntilValue = entitlement?.validUntil || entitlement?.currentPeriodEnd;
      newState[marketId] = {
        hasAccess: hasEntitlement,
        accessLevel: entitlement?.accessLevel,
        validUntil: validUntilValue ? (validUntilValue instanceof Date ? validUntilValue.toISOString() : validUntilValue) : undefined,
      };
    }

    return newState;
  }, [user, enabled, memoizedMarketIds, entitlementsData]);

  // Update access state when computed state changes
  useEffect(() => {
    if (Object.keys(computedAccessState).length > 0) {
      setAccessState(computedAccessState);
    }
  }, [computedAccessState]);

  const checkAccess = useCallback(async (bypassCache = false) => {
    // This is now a no-op since we compute access from entitlements
    // Keep it for API compatibility
    return;
  }, []);

  // No longer needed - access is computed from entitlements
  // Initial load and market ID changes are handled by useMemo

  // Set up refetch interval if specified
  // Note: Refetch is handled by the useUserEntitlements hook
  useEffect(() => {
    if (refetchInterval && refetchInterval > 0) {
      refetchIntervalRef.current = window.setInterval(() => {
        // Access will be recomputed when entitlements refetch
        // This is now a no-op but kept for API compatibility
        checkAccess(true);
      }, refetchInterval);

      return () => {
        if (refetchIntervalRef.current) {
          window.clearInterval(refetchIntervalRef.current);
        }
      };
    }
  }, [refetchInterval, checkAccess]);

  const hasAccessToMarket = useCallback((marketId: number): boolean => {
    return accessState[marketId]?.hasAccess || false;
  }, [accessState]);

  const getAccessLevel = useCallback((marketId: number): string | undefined => {
    return accessState[marketId]?.accessLevel;
  }, [accessState]);

  const getValidUntil = useCallback((marketId: number): Date | undefined => {
    const validUntil = accessState[marketId]?.validUntil;
    return validUntil ? new Date(validUntil) : undefined;
  }, [accessState]);

  const invalidateCache = useCallback(() => {
    if (user) {
      marketAccessCache.invalidate(user.id);
      checkAccess(true); // Bypass cache on refetch
    }
  }, [user, checkAccess]);

  const refetch = useCallback(() => {
    return checkAccess(true);
  }, [checkAccess]);

  return {
    accessState,
    hasAccessToMarket,
    getAccessLevel,
    getValidUntil,
    loading: loading || entitlementsLoading,
    error,
    refetch,
    invalidateCache,
    isChecking: (loading || entitlementsLoading) && Object.keys(accessState).length === 0,
    isRefetching: (loading || entitlementsLoading) && Object.keys(accessState).length > 0,
  };
}

// Convenience hook for single market
export function useSingleMarketAccess(
  marketId: number | undefined,
  options?: UseMarketAccessOptions
) {
  const marketIds = useMemo(() => marketId ? [marketId] : [], [marketId]);
  const result = useMarketAccess(marketIds, options);

  return {
    hasAccess: marketId ? result.hasAccessToMarket(marketId) : false,
    accessLevel: marketId ? result.getAccessLevel(marketId) : undefined,
    validUntil: marketId ? result.getValidUntil(marketId) : undefined,
    loading: result.loading,
    error: result.error,
    refetch: result.refetch,
  };
}